import type { ComponentType, ReactNode, SVGProps } from "react";
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/cn";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type NavIcon = ComponentType<{ className?: string } & SVGProps<SVGSVGElement>>;

export interface NavItem {
  title: string;
  /**
   * Static path, or a function that resolves the current target each render
   * (used for cross-section links that remember where the user left off).
   */
  href: string | (() => string);
  icon: NavIcon;
  badge?: string;
  disabled?: boolean;
  end?: boolean;
  /**
   * Optional escape hatch — when provided, NavMain renders the result of
   * `render(collapsed)` instead of the default link. The `title` and `href`
   * are still used for accessibility and as a fallback.
   */
  render?: (collapsed: boolean) => ReactNode;
}

export interface NavGroup {
  items: NavItem[];
  /** When true, items in this group are indented to signal they're nested
   *  under the section above (e.g. space-scoped items under Spaces). */
  indent?: boolean;
}

interface NavMainProps {
  groups: NavGroup[];
  collapsed?: boolean;
}

export function NavMain({ groups, collapsed = false }: NavMainProps) {
  return (
    <nav className="flex flex-1 flex-col gap-2 px-2">
      {groups.map((group, groupIdx) => (
        <div
          key={groupIdx}
          className={cn(
            "space-y-1",
            // Last group (cross-section link) sticks to the bottom of the rail.
            groupIdx === groups.length - 1 && "mt-auto",
            // Subtle divider between consecutive in-flow groups so cross-space
            // sections (Events, Settings) don't visually run into the
            // space-scoped items above.
            groupIdx > 0 &&
              groupIdx !== groups.length - 1 &&
              "border-t border-border/50 pt-2 mt-1",
          )}
        >
          {group.items.map((item) => {
            if (item.render) {
              return <div key={item.title}>{item.render(collapsed)}</div>;
            }
            const Icon = item.icon;
            const href = typeof item.href === "function" ? item.href() : item.href;
            const link = (
              <NavLink
                key={item.title}
                to={href}
                end={item.end ?? href === "/"}
                className={cn(
                  "group flex items-center gap-3 rounded-md py-2 text-sm font-medium transition-colors",
                  "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                  "aria-[current=page]:text-primary",
                  item.disabled && "pointer-events-none opacity-50",
                  collapsed ? "justify-center px-2" : group.indent ? "pl-8 pr-3" : "px-3",
                )}
              >
                <Icon className="h-5 w-5 shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1">{item.title}</span>
                    {item.badge && (
                      <span className="ml-auto rounded-full bg-primary-100 dark:bg-primary-900/30 px-2 py-0.5 text-xs font-medium text-primary-600">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );

            if (collapsed) {
              return (
                <Tooltip key={item.title} delayDuration={0}>
                  <TooltipTrigger asChild>{link}</TooltipTrigger>
                  <TooltipContent side="right">
                    <p>{item.title}</p>
                    {item.badge && (
                      <p className="text-xs text-muted-foreground">{item.badge}</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              );
            }

            return link;
          })}
        </div>
      ))}
    </nav>
  );
}
